import {CommandBarButton, Dropdown, MessageBarType, TextField} from "office-ui-fabric-react";
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import SchoolLicense from "../models/SchoolLicense";
import { UserAccessLevel, userAccessLevelIsAtLeast } from "../models/UserAccessRecord";
import Actions from "../redux/Actions";
import { IRootState } from "../store";
import DispatchProps from "../utils/DispatchProps";
import ApiService from "./ApiService";
import {License, LICENSE_DROPDOWN_OPTIONS} from "../models/School";

interface UpdateSchoolLicenseProps {
    tenantId: string;
    schoolCode: string;

    usersCdbAccessLevel?: UserAccessLevel,
    apiService: ApiService;
}

interface UpdateSchoolLicenseState {
    saveButtonDisabled: boolean;

    license?: License;
    licenseReference?: string;
    licenseEndYear?: string;
    licenseEndMonth?: string;

    yearErrorMessage?: string;
    monthErrorMessage?: string;
}

class UpdateSchoolLicense extends Component<UpdateSchoolLicenseProps & DispatchProps, UpdateSchoolLicenseState> {

    constructor(props: UpdateSchoolLicenseProps & DispatchProps) {
        super(props);

        this.state = {
            saveButtonDisabled: false
        };
    }

    async componentDidMount(): Promise<void> {
        if (this.props.tenantId && this.props.schoolCode)
        {
            await this.loadLicense();
        }
    }
    
    async componentDidUpdate(prevProps: Readonly<UpdateSchoolLicenseProps & DispatchProps>, prevState: Readonly<UpdateSchoolLicenseState>, snapshot?: any): Promise<void> {
        if (prevProps.tenantId != this.props.tenantId || prevProps.schoolCode != this.props.schoolCode)
        {
            await this.loadLicense();
        }
    }

    private async loadLicense(): Promise<void> {
        try {
            this.setState({ saveButtonDisabled: true });

            var existingLicense = await this.props.apiService.getLicenseAdmin(this.props.tenantId, this.props.schoolCode);
            
            this.setState({
                license: existingLicense.license,
                licenseReference: existingLicense.licenseReference
            });
            
            if (existingLicense != undefined &&
                existingLicense.licenseEndDate != undefined) {

                var date = new Date(existingLicense.licenseEndDate);
                if (!isNaN(date.getTime())) {
                    this.setState({ licenseEndMonth: (date.getMonth() + 1).toString() });
                    this.setState({ licenseEndYear: date.getFullYear().toString() });
                }
            }
        }
        catch (e) {
            console.log(e);
            this.props.dispatch(Actions.showMessageBar("Unable to load license details", MessageBarType.error));
        }
        this.setState({ saveButtonDisabled: false });
    }

    protected submitForm = async () => {
        try {

            if (!this.isYearValid(this.state.licenseEndYear) ||
                !this.isMonthValid(this.state.licenseEndMonth)) {
                return;
            }

            var year = Number(this.state.licenseEndYear);
            var month = Number(this.state.licenseEndMonth);

            var date = new Date();
            date.setUTCFullYear(year, month - 1, 1);
            date.setUTCHours(0, 0, 0, 0);

            var schoolLicense: SchoolLicense = {
                tenantID: this.props.tenantId,
                schoolCode: this.props.schoolCode,
                license: this.state.license!,
                licenseReference: this.state.licenseReference!,
                licenseEndDate: date
            }

            this.setState({ saveButtonDisabled: true });

            await this.props.apiService.updateLicenseAdmin(schoolLicense);

            this.props.dispatch(Actions.showMessageBar("License updated successfully", MessageBarType.success));
        }
        catch (e) {
            console.log(e);
            this.props.dispatch(Actions.showMessageBar("License update save request failed", MessageBarType.error));
        }
        this.setState({ saveButtonDisabled: false });
    };

    private isDisabled = () => {
        return this.state.saveButtonDisabled || !userAccessLevelIsAtLeast("admin", this.props.usersCdbAccessLevel);
    }

    private onYearChange = (_: any, value: string | undefined) => {
        this.setState({ yearErrorMessage: undefined });
        this.setState({ licenseEndYear: value })

        if (!this.isYearValid(value)) {
            this.setState({ yearErrorMessage: "Must be a valid year. e.g. 2021" });
        }
    }

    private onMonthChange = (_: any, value: string | undefined) => {
        this.setState({ monthErrorMessage: undefined });
        this.setState({ licenseEndMonth: value })

        if (!this.isMonthValid(value)) {
            this.setState({ monthErrorMessage: "Must be a valid month. e.g. 12" });
        }
    }

    private isYearValid(year: string | undefined): boolean {
        return year != undefined && /^\d{4}$/.test(year);
    }

    private isMonthValid(month: string | undefined): boolean {
        return month != undefined && /^(0?[1-9]|1[012])$/.test(month);
    }

    render(): JSX.Element {
        return (
            <Fragment>
                <div className="card card">
                    <div className="card__header">
                        <span className={"card__title"}>Update License</span>

                        <CommandBarButton
                            iconProps={{ iconName: "save" }}
                            text="Save"
                            disabled={this.isDisabled()}
                            onClick={this.submitForm}
                        />
                    </div>
                    <div className="card__content flex-column">

                        <TextField className="card__input"
                                   label={"License Reference"}
                                   description={"Internal CDB code which identifies the school's license, should usually match the value in other systems such as Freshdesk and Pipedrive."}
                                   value={this.state.licenseReference}
                                   onChange={(e,newValue)=> {
                                       this.setState({
                                           licenseReference: newValue!
                                       });
                                   }}
                        />
                        
                        <Dropdown
                            selectedKey={this.state.license}
                            label={"License"}
                            placeholder={"Unknown"}
                            options={LICENSE_DROPDOWN_OPTIONS}
                            onChange={(e: any, option: any) => {
                                if (option) {
                                    this.setState({
                                        license: option.key
                                    });
                                }
                            }}
                        />
                        
                        <TextField className="card__input"
                            label="End Year"
                            disabled={this.isDisabled()}
                            value={this.state.licenseEndYear}
                            errorMessage={this.state.yearErrorMessage}
                            onChange={this.onYearChange} />

                        <TextField className="card__input"
                            label="End Month"
                            disabled={this.isDisabled()}
                            value={this.state.licenseEndMonth}
                            errorMessage={this.state.monthErrorMessage}
                            onChange={this.onMonthChange} />

                    </div>
                </div>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: { schoolCode: string, tenantId: string }): UpdateSchoolLicenseProps => {
        return {
            schoolCode: ownProps.schoolCode,
            tenantId: ownProps.tenantId,
            usersCdbAccessLevel: state.auth.usersCdbAccessLevel,
            apiService: state.auth.apiService,
        };
    }
)(UpdateSchoolLicense)